import { useCallback, useEffect, useRef, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { invalidateBusinessName } from '@/hooks/useBusinessName';
import { logger } from '@/logger';

import type {
	AgeRange,
	BusinessProfile,
	IdealCustomerProfileItem,
	ImageUrl,
	PositioningItem,
	Product,
} from '../../../../bridge/types/business-profile';

interface CosmosDoc {
	business_profile: BusinessProfile;
	updated_at?: string;
}

const INDEXED_PATH =
	/^(products\.product|ideal_customer_profiles)\.(\d+)\.(.+)$/;

const EMPTY_PRODUCT: Product = {
	name: { value: null, inferred: false },
	description: { value: null, inferred: false },
	benefits: { value: null, inferred: false },
	features: { value: null, inferred: false },
	images: { value: null, inferred: false },
	price: { value: null, inferred: false },
};

export function buildPartialUpdate(
	dotPath: string,
	value: unknown,
): Record<string, unknown> {
	const parts = dotPath.split('.');
	let current: Record<string, unknown> = {};
	const root = current;
	for (let i = 0; i < parts.length - 1; i++) {
		const next: Record<string, unknown> = {};
		current[parts[i]] = next;
		current = next;
	}
	current[parts[parts.length - 1]] = { value, inferred: false };
	return root;
}

function buildArrayPatch(
	root: 'products.product' | 'ideal_customer_profiles',
	updated: unknown[],
): Record<string, unknown> {
	if (root === 'products.product') {
		return { products: { product: updated } };
	}
	return { ideal_customer_profiles: updated };
}

function spliceProduct(
	products: Product[],
	index: number,
	leaf: string,
	wrappedValue: unknown,
): Product[] {
	return products.map((p, i) => {
		if (i !== index) return p;
		return { ...p, [leaf]: wrappedValue } as Product;
	});
}

function spliceIcp(
	items: IdealCustomerProfileItem[],
	index: number,
	leaf: string,
	wrappedValue: unknown,
): IdealCustomerProfileItem[] {
	return items.map((item, i) => {
		if (i !== index) return item;
		return { ...item, [leaf]: wrappedValue } as IdealCustomerProfileItem;
	});
}

export function useBusinessProfileApi(userId: string | null) {
	const bridgewayApi = useBridgewayApi();
	const [profile, setProfile] = useState<BusinessProfile | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const profileRef = useRef<BusinessProfile | null>(null);

	useEffect(() => {
		profileRef.current = profile;
	}, [profile]);

	const fetchProfile = useCallback(async () => {
		if (!userId) return;
		try {
			setLoading(true);
			setError(null);
			const doc = await bridgewayApi.get<CosmosDoc>(
				`/api/user/${userId}/business-profile`,
			);
			setProfile(doc.business_profile);
		} catch {
			setProfile(null);
			setError(
				'No business profile found. Complete onboarding to generate one.',
			);
		} finally {
			setLoading(false);
		}
	}, [userId, bridgewayApi]);

	useEffect(() => {
		void fetchProfile();
	}, [fetchProfile]);

	const sendPatch = useCallback(
		(patch: Record<string, unknown>, errLabel: string) => {
			if (!userId) return;
			bridgewayApi
				.patch(`/api/user/${userId}/business-profile`, patch)
				.catch((err: unknown) => {
					logger.error(errLabel, err);
					void fetchProfile();
				});
		},
		[userId, bridgewayApi, fetchProfile],
	);

	const applyOptimistic = useCallback(
		(mutator: (p: BusinessProfile) => BusinessProfile) => {
			if (profileRef.current) {
				profileRef.current = mutator(profileRef.current);
			}
			setProfile((prev) => (prev ? mutator(prev) : prev));
		},
		[],
	);

	const onSave = useCallback(
		(path: string, value: string) => {
			const indexed = path.match(INDEXED_PATH);
			if (indexed) {
				const [, root, idxStr, leaf] = indexed;
				const idx = Number(idxStr);
				const wrapped = { value, inferred: false };
				const current = profileRef.current;
				if (!current) return;
				if (root === 'products.product') {
					const arr = current.products?.product ?? [];
					const updated = spliceProduct(arr, idx, leaf, wrapped);
					applyOptimistic((prev) => ({
						...prev,
						products: { ...prev.products, product: updated },
					}));
					sendPatch(
						buildArrayPatch('products.product', updated),
						'Failed to save product field',
					);
				} else {
					const arr = current.ideal_customer_profiles ?? [];
					const updated = spliceIcp(arr, idx, leaf, wrapped);
					applyOptimistic((prev) => ({
						...prev,
						ideal_customer_profiles: updated,
					}));
					sendPatch(
						buildArrayPatch('ideal_customer_profiles', updated),
						'Failed to save ICP field',
					);
				}
				return;
			}
			const patch = buildPartialUpdate(path, value);
			applyOptimistic((prev) => deepMerge(prev, patch) as BusinessProfile);
			sendPatch(patch, 'Failed to save profile field');
			// The chat greeting and Settings label read the name from a separate
			// fetch-once cache keyed by user. Drop it here — the one place the name
			// is actually persisted — so those surfaces refetch on their next mount.
			if (userId && path === 'my_business.business_name') {
				invalidateBusinessName(userId);
			}
		},
		[applyOptimistic, sendPatch, userId],
	);

	const onArraySave = useCallback(
		(path: string, value: string[]) => {
			const indexed = path.match(INDEXED_PATH);
			if (indexed) {
				const [, root, idxStr, leaf] = indexed;
				const idx = Number(idxStr);
				const wrapped = { value, inferred: false };
				const current = profileRef.current;
				if (!current) return;
				if (root === 'products.product') {
					const arr = current.products?.product ?? [];
					const updated = spliceProduct(arr, idx, leaf, wrapped);
					applyOptimistic((prev) => ({
						...prev,
						products: { ...prev.products, product: updated },
					}));
					sendPatch(
						buildArrayPatch('products.product', updated),
						'Failed to save product array field',
					);
				} else {
					const arr = current.ideal_customer_profiles ?? [];
					const updated = spliceIcp(arr, idx, leaf, wrapped);
					applyOptimistic((prev) => ({
						...prev,
						ideal_customer_profiles: updated,
					}));
					sendPatch(
						buildArrayPatch('ideal_customer_profiles', updated),
						'Failed to save ICP array field',
					);
				}
				return;
			}
			const patch = buildPartialUpdate(path, value);
			applyOptimistic((prev) => deepMerge(prev, patch) as BusinessProfile);
			sendPatch(patch, 'Failed to save profile array field');
		},
		[applyOptimistic, sendPatch],
	);

	const onNumberSave = useCallback(
		(path: string, value: number) => {
			const indexed = path.match(INDEXED_PATH);
			if (indexed) {
				const [, root, idxStr, leaf] = indexed;
				const idx = Number(idxStr);
				const wrapped = { value, inferred: false };
				const current = profileRef.current;
				if (!current) return;
				if (root === 'ideal_customer_profiles') {
					const arr = current.ideal_customer_profiles ?? [];
					const updated = spliceIcp(arr, idx, leaf, wrapped);
					applyOptimistic((prev) => ({
						...prev,
						ideal_customer_profiles: updated,
					}));
					sendPatch(
						buildArrayPatch('ideal_customer_profiles', updated),
						'Failed to save ICP number field',
					);
					return;
				}
				const arr = current.products?.product ?? [];
				const updated = spliceProduct(arr, idx, leaf, wrapped);
				applyOptimistic((prev) => ({
					...prev,
					products: { ...prev.products, product: updated },
				}));
				sendPatch(
					buildArrayPatch('products.product', updated),
					'Failed to save product number field',
				);
				return;
			}
			const patch = buildPartialUpdate(path, value);
			applyOptimistic((prev) => deepMerge(prev, patch) as BusinessProfile);
			sendPatch(patch, 'Failed to save profile number field');
		},
		[applyOptimistic, sendPatch],
	);

	const onIcpAgeSave = useCallback(
		(index: number, value: AgeRange) => {
			const wrapped = { value, inferred: false };
			const current = profileRef.current;
			if (!current) return;
			const arr = current.ideal_customer_profiles ?? [];
			const updated = spliceIcp(arr, index, 'age', wrapped);
			applyOptimistic((prev) => ({
				...prev,
				ideal_customer_profiles: updated,
			}));
			sendPatch(
				buildArrayPatch('ideal_customer_profiles', updated),
				'Failed to save ICP age',
			);
		},
		[applyOptimistic, sendPatch],
	);

	const onPositioningSave = useCallback(
		(value: PositioningItem[]) => {
			const patch = {
				brand_identity: {
					positioning: { value, inferred: false },
				},
			};
			applyOptimistic((prev) => ({
				...prev,
				brand_identity: {
					...prev.brand_identity,
					positioning: { ...prev.brand_identity.positioning, value },
				},
			}));
			sendPatch(patch, 'Failed to save positioning');
		},
		[applyOptimistic, sendPatch],
	);

	const onImageUrlsSave = useCallback(
		(value: ImageUrl[], baseValue: ImageUrl[]) => {
			applyOptimistic((prev) => ({
				...prev,
				brand_identity: { ...prev.brand_identity, image_urls: value },
			}));
			if (!userId) return;
			// PUT (not the generic PATCH) so the backend can reconcile against
			// whatever the live document holds instead of blindly overwriting the
			// whole array -- a concurrent upload elsewhere (chat, another tab)
			// between our fetch (baseValue) and this save must survive. See
			// api/routes/business_profile.py:save_imagery.
			bridgewayApi
				.put(`/api/user/${userId}/business-profile/imagery`, {
					image_urls: value,
					base_image_urls: baseValue,
				})
				.catch((err: unknown) => {
					logger.error('Failed to save imagery', err);
					void fetchProfile();
				});
		},
		[applyOptimistic, userId, bridgewayApi, fetchProfile],
	);

	const onAddProduct = useCallback(() => {
		const current = profileRef.current;
		if (!current) return;
		const arr = current.products?.product ?? [];
		const updated = [...arr, EMPTY_PRODUCT];
		applyOptimistic((prev) => ({
			...prev,
			products: { ...prev.products, product: updated },
		}));
		sendPatch(
			buildArrayPatch('products.product', updated),
			'Failed to add product',
		);
	}, [applyOptimistic, sendPatch]);

	const onRemoveProduct = useCallback(
		(index: number) => {
			const current = profileRef.current;
			if (!current) return;
			const arr = current.products?.product ?? [];
			const updated = arr.filter((_, i) => i !== index);
			applyOptimistic((prev) => ({
				...prev,
				products: { ...prev.products, product: updated },
			}));
			sendPatch(
				buildArrayPatch('products.product', updated),
				'Failed to remove product',
			);
		},
		[applyOptimistic, sendPatch],
	);

	return {
		profile,
		loading,
		error,
		refetch: fetchProfile,
		onSave,
		onArraySave,
		onNumberSave,
		onIcpAgeSave,
		onPositioningSave,
		onImageUrlsSave,
		onAddProduct,
		onRemoveProduct,
	};
}

function deepMerge(target: unknown, source: unknown): unknown {
	if (
		typeof target !== 'object' ||
		typeof source !== 'object' ||
		target === null ||
		source === null ||
		Array.isArray(target) ||
		Array.isArray(source)
	) {
		return source;
	}
	const result = { ...(target as Record<string, unknown>) };
	for (const key of Object.keys(source as Record<string, unknown>)) {
		result[key] = deepMerge(
			result[key],
			(source as Record<string, unknown>)[key],
		);
	}
	return result;
}
